import React, { useEffect, useMemo, useRef, useState } from 'react';
import { format, formatDistanceToNow, isToday, isYesterday } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '@/components/ui/dialog';
import {
  StickyNote, Plus, Search, CheckCircle2, Circle, Trash2, Pencil,
  ChevronDown, ChevronRight, User, Clock, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const dayLabel = (dateStr) => {
  const d = new Date(dateStr);
  if (isToday(d)) return 'Today';
  if (isYesterday(d)) return 'Yesterday';
  return format(d, 'PP'); // e.g. "Apr 22, 2026"
};

export default function NotesFab() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newText, setNewText] = useState('');
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({}); // { groupKey: bool }

  const fetchNotes = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/notes`, { credentials: 'include' });
      if (res.ok) setNotes(await res.json());
    } catch (e) {}
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) fetchNotes(); }, [open]);

  const addNote = async () => {
    const text = newText.trim();
    if (!text) return;
    setAdding(true);
    // Optimistic add
    const tempId = `tmp-${Date.now()}`;
    const nowIso = new Date().toISOString();
    const optimistic = {
      id: tempId, title: text, content: '', category: 'discussion',
      is_done: false, author_email: user?.email, author_name: user?.name || user?.email?.split('@')[0],
      created_at: nowIso, updated_at: nowIso,
    };
    setNotes(prev => [optimistic, ...prev]);
    setNewText('');
    try {
      const res = await fetch(`${API_URL}/api/notes`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: text, content: '', category: 'discussion' }),
      });
      if (!res.ok) throw new Error('Failed');
      const saved = await res.json();
      setNotes(prev => prev.map(n => n.id === tempId ? saved : n));
    } catch (e) {
      setNotes(prev => prev.filter(n => n.id !== tempId));
      toast.error('Failed to add note');
    } finally { setAdding(false); }
  };

  const toggleDone = async (note) => {
    // Optimistic
    setNotes(prev => prev.map(n => n.id === note.id ? { ...n, is_done: !note.is_done } : n));
    try {
      await fetch(`${API_URL}/api/notes/${note.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_done: !note.is_done }),
      });
    } catch (e) {
      setNotes(prev => prev.map(n => n.id === note.id ? { ...n, is_done: note.is_done } : n));
      toast.error('Failed');
    }
  };

  const commitEdit = async (note) => {
    const text = editingText.trim();
    if (!text || text === note.title) { setEditingId(null); return; }
    const prev = note.title;
    setNotes(ps => ps.map(n => n.id === note.id ? { ...n, title: text, updated_at: new Date().toISOString() } : n));
    setEditingId(null);
    try {
      const res = await fetch(`${API_URL}/api/notes/${note.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: text }),
      });
      if (!res.ok) throw new Error('Failed');
    } catch (e) {
      setNotes(ps => ps.map(n => n.id === note.id ? { ...n, title: prev } : n));
      toast.error('Failed to update');
    }
  };

  const deleteNote = async (note) => {
    if (!window.confirm('Delete this note?')) return;
    const prev = notes;
    setNotes(ps => ps.filter(n => n.id !== note.id));
    try {
      const res = await fetch(`${API_URL}/api/notes/${note.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
    } catch (e) {
      setNotes(prev);
      toast.error('Failed to delete');
    }
  };

  const filtered = useMemo(() => {
    if (!search) return notes;
    const s = search.toLowerCase();
    return notes.filter(n =>
      (n.title || '').toLowerCase().includes(s) ||
      (n.author_name || '').toLowerCase().includes(s)
    );
  }, [notes, search]);

  // Group by Date + Author
  const groups = useMemo(() => {
    const map = new Map();
    for (const n of filtered) {
      const day = dayLabel(n.created_at);
      const author = n.author_name || (n.author_email || '').split('@')[0] || 'Someone';
      const key = `${day}|${author}`;
      if (!map.has(key)) map.set(key, { key, day, author, notes: [], latest: n.created_at });
      const g = map.get(key);
      g.notes.push(n);
      if (n.created_at > g.latest) g.latest = n.created_at;
    }
    // Sort groups by latest timestamp desc
    return Array.from(map.values()).sort((a, b) => (a.latest < b.latest ? 1 : -1));
  }, [filtered]);

  const openCount = notes.filter(n => !n.is_done).length;

  return (
    <>
      {/* Floating Action Button */}
      <button
        onClick={() => setOpen(true)}
        className="notes-fab fixed bottom-6 right-6 z-40 group"
        aria-label="Open Notes"
        data-testid="notes-fab"
      >
        <span className="notes-fab-ripple" />
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-amber-400 to-amber-500 text-white shadow-xl shadow-amber-500/40 hover:scale-110 active:scale-95 transition-transform duration-200">
          <StickyNote className="h-6 w-6 notes-fab-wave" />
          {openCount > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white shadow-md">
              {openCount}
            </span>
          )}
        </span>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-slate-900 text-white text-xs font-medium px-2.5 py-1.5 rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity shadow-lg">
          Team Notes
        </span>
      </button>

      {/* Modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden">
          <DialogHeader className="p-5 border-b border-slate-100 space-y-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-500 flex items-center justify-center shadow-md shadow-amber-500/25">
                  <StickyNote className="h-4 w-4 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-lg">Team Notes</DialogTitle>
                  <p className="text-xs text-slate-500">Running checklist · {openCount} open · {notes.length} total</p>
                </div>
              </div>
            </div>
          </DialogHeader>

          <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Quick add */}
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-2 rounded-lg border border-slate-200 px-3 focus-within:border-[#2EB4AC] focus-within:ring-2 focus-within:ring-[#2EB4AC]/20 transition">
                <Plus className="h-4 w-4 text-slate-400 flex-shrink-0" />
                <input
                  placeholder="Take a note, add an action item..."
                  value={newText}
                  onChange={(e) => setNewText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addNote(); }}
                  disabled={adding}
                  className="w-full h-10 bg-transparent outline-none text-sm placeholder:text-slate-400"
                  data-testid="fab-note-input"
                  autoFocus
                />
              </div>
              <Button onClick={addNote} disabled={adding || !newText.trim()} className="h-10 bg-[#2EB4AC] hover:bg-[#25908A] text-white" data-testid="fab-add-btn">
                Add
              </Button>
            </div>

            {/* Search */}
            {notes.length > 0 && (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search notes or author..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9 bg-slate-50 border-slate-200"
                  data-testid="fab-search"
                />
                {search && (
                  <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}

            {/* Groups */}
            {loading ? (
              <div className="text-center py-8 text-sm text-slate-400">Loading...</div>
            ) : groups.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-xl">
                <StickyNote className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">{search ? 'No matching notes' : 'No notes yet — add one above'}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {groups.map(g => {
                  const expanded = expandedGroups[g.key] === true;
                  const openInGroup = g.notes.filter(n => !n.is_done).length;
                  return (
                    <div key={g.key} className="border border-slate-200 rounded-lg overflow-hidden">
                      <button
                        onClick={() => setExpandedGroups(p => ({ ...p, [g.key]: !expanded }))}
                        className="w-full flex items-center gap-2 px-3 py-2.5 bg-slate-50 hover:bg-slate-100 transition text-left"
                        data-testid={`fab-group-${g.key}`}
                      >
                        {expanded ? <ChevronDown className="h-3.5 w-3.5 text-slate-500" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-500" />}
                        <div className="w-6 h-6 rounded-full bg-[#2EB4AC]/15 text-[#2EB4AC] flex items-center justify-center text-[10px] font-bold">
                          {(g.author || '').charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-slate-800">{g.author}</p>
                          <p className="text-[10px] text-slate-500">{g.day} · {g.notes.length} {g.notes.length === 1 ? 'note' : 'notes'}{openInGroup > 0 && <span className="ml-1 text-amber-600 font-medium">· {openInGroup} open</span>}</p>
                        </div>
                      </button>
                      {expanded && (
                        <div className="divide-y divide-slate-100">
                          {g.notes.map(n => (
                            <NoteRow
                              key={n.id}
                              note={n}
                              currentUserEmail={user?.email}
                              isEditing={editingId === n.id}
                              editingText={editingText}
                              setEditingText={setEditingText}
                              onToggle={toggleDone}
                              onStartEdit={(note) => { setEditingId(note.id); setEditingText(note.title || ''); }}
                              onCommitEdit={commitEdit}
                              onCancelEdit={() => setEditingId(null)}
                              onDelete={deleteNote}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50">
            <p className="text-[11px] text-slate-500 text-center flex items-center justify-center gap-1.5">
              <CheckCircle2 className="h-3 w-3 text-emerald-500" /> All changes auto-save · Press Enter to add
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const NoteRow = ({ note, currentUserEmail, isEditing, editingText, setEditingText, onToggle, onStartEdit, onCommitEdit, onCancelEdit, onDelete }) => {
  const isOwner = currentUserEmail && note.author_email === currentUserEmail;
  const inputRef = useRef(null);
  useEffect(() => { if (isEditing && inputRef.current) inputRef.current.focus(); }, [isEditing]);

  return (
    <div className={`group flex items-start gap-2 px-4 py-2.5 hover:bg-slate-50 transition ${note.is_done ? 'opacity-60' : ''}`} data-testid={`fab-note-${note.id}`}>
      <button onClick={() => onToggle(note)} className="mt-0.5 flex-shrink-0 text-slate-400 hover:text-[#2EB4AC] transition" title={note.is_done ? 'Mark as open' : 'Mark as done'} data-testid={`fab-toggle-${note.id}`}>
        {note.is_done ? <CheckCircle2 className="h-4 w-4 text-[#2EB4AC]" /> : <Circle className="h-4 w-4" />}
      </button>
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <input
            ref={inputRef}
            value={editingText}
            onChange={(e) => setEditingText(e.target.value)}
            onBlur={() => onCommitEdit(note)}
            onKeyDown={(e) => { if (e.key === 'Enter') onCommitEdit(note); if (e.key === 'Escape') onCancelEdit(); }}
            className="w-full text-sm bg-transparent border-b border-[#2EB4AC] outline-none py-0.5"
            data-testid={`fab-edit-${note.id}`}
          />
        ) : (
          <p
            className={`text-sm text-slate-900 ${note.is_done ? 'line-through text-slate-500' : ''} whitespace-pre-wrap break-words`}
            onDoubleClick={() => isOwner && onStartEdit(note)}
          >
            {note.title || <span className="italic text-slate-400">(empty)</span>}
          </p>
        )}
        <p className="text-[10px] text-slate-400 mt-0.5 flex items-center gap-1" title={format(new Date(note.created_at), 'PPp')}>
          <Clock className="h-2.5 w-2.5" />{formatDistanceToNow(new Date(note.created_at), { addSuffix: true })}
        </p>
      </div>
      {isOwner && !isEditing && (
        <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition">
          <Button variant="ghost" size="sm" onClick={() => onStartEdit(note)} className="h-6 w-6 p-0" title="Edit">
            <Pencil className="h-3 w-3 text-slate-400" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onDelete(note)} className="h-6 w-6 p-0 hover:bg-red-50" title="Delete">
            <Trash2 className="h-3 w-3 text-red-400" />
          </Button>
        </div>
      )}
    </div>
  );
};
